import { useState, useEffect, useCallback } from 'react';
import { backgroundsApi } from '../lib/api';
import defaultBg from '../assets/background/default-bg.svg';

/**
 * Custom hook for managing background image.
 * Fetches active background (global or user) and applies it to the page.
 * Priority: Global background > User background > Default background
 * @param {boolean} shouldFetch - Whether to fetch the background (only when auth is ready)
 */
export const useBackgroundImage = (shouldFetch = true) => {
  const [background, setBackground] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActiveBackground = useCallback(async () => {
    // Don't fetch if auth is not ready
    if (!shouldFetch) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await backgroundsApi.getActive();
      const backgroundData = response.data.data;



      if (backgroundData) {
        // Construct full URL
        const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const fullUrl = `${API_BASE_URL}${backgroundData.url}`;

        setBackground({
          ...backgroundData,
          fullUrl,
        });
      } else {

        setBackground({
          fullUrl: defaultBg,
          isDefault: true
        });
      }
    } catch (err) {
      console.error('[Background] Error fetching active background:', err);
      // Fallback to default on error
      setBackground({
        fullUrl: defaultBg,
        isDefault: true
      });
      setError('Failed to load background');
    } finally {
      setLoading(false);
    }
  }, [shouldFetch]);

  useEffect(() => {
    fetchActiveBackground();
  }, [fetchActiveBackground]);

  // Apply background to document body (skip if using default theme-aware background)
  useEffect(() => {
    // Always add portal-body class for fixed background
    document.body.classList.add('portal-body');

    // If it's the default background, add class to hide gradient
    if (background?.isDefault) {

      document.body.classList.add('has-custom-background');
      return () => {
        document.body.classList.remove('has-custom-background');
      };
    }

    if (background?.fullUrl) {

      document.body.style.backgroundImage = `url("${background.fullUrl}")`;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center center';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.setProperty('background-attachment', 'fixed', 'important');

    } else {

      // Reset to default
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
      document.body.classList.remove('has-custom-background');
    };
  }, [background]);

  return {
    background,
    loading,
    error,
    refetch: fetchActiveBackground,
    useThemeAwareBackground: background?.isDefault || false,
  };
};
